import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { createPortal } from 'react-dom'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  position: relative;
`

const Label = styled.label`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.muted};
`

const Trigger = styled.div`
  width: 100%;
  border: 1px solid ${({ theme, $isOpen }) => $isOpen ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  min-height: 40px;
  padding: 4px 12px;
  background: white;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  box-shadow: ${({ theme, $isOpen }) => $isOpen ? `0 0 0 3px ${theme.colors.primary}33` : 'none'};
  transition: all 0.2s ease;
  user-select: none;
`

const OptionsList = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  max-height: 240px;
  overflow-y: auto;
  z-index: 99999;
  padding: 4px;
`

const Option = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ $isSelected, theme }) => $isSelected ? theme.colors.background : 'transparent'};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f1f5f9;
  }
`

const CheckBox = styled.input`
  margin: 0;
  cursor: pointer;
`

const Arrow = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.muted};
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease;
  margin-left: 8px;
`

const SelectedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const Tag = styled.span`
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
`

export function MultiSelect({ label, value = [], onChange, options = [], disabled, placeholder = 'Select...', style, className, showTags = true }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const optionsRef = useRef(null)
  const searchInputRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current && !containerRef.current.contains(event.target) &&
        (!optionsRef.current || !optionsRef.current.contains(event.target))
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    function handleResize() { setIsOpen(false) }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  const handleOpen = () => {
    if (disabled) return
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownMaxHeight = 248
      const openAbove = spaceBelow < dropdownMaxHeight && rect.top > spaceBelow
      if (openAbove) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 4, top: 'auto', left: rect.left, width: rect.width, openAbove: true })
      } else {
        setDropdownPos({ top: rect.bottom + 4, bottom: 'auto', left: rect.left, width: rect.width, openAbove: false })
      }
    }
    setIsOpen(!isOpen)
  }

  const handleSelect = (val) => {
    if (disabled) return
    const newValue = value.includes(val)
      ? value.filter(v => v !== val)
      : [...value, val]
    onChange && onChange(newValue)
  }

  const selectedOptions = options.filter(opt => {
    const val = typeof opt === 'object' ? opt.value : opt
    return value.includes(val)
  })

  const filteredOptions = options.filter(opt => {
    const lbl = typeof opt === 'object' ? opt.label : String(opt)
    return String(lbl).toLowerCase().includes(searchTerm.toLowerCase())
  })

  return (
    <Wrapper ref={containerRef} style={style} className={className}>
      {label && <Label>{label}</Label>}
      <Trigger
        ref={triggerRef}
        $isOpen={isOpen}
        $disabled={disabled}
        onClick={handleOpen}
      >
        {selectedOptions.length > 0 ? (
          showTags ? (
            <SelectedTags>
              {selectedOptions.length <= 3 ? (
                selectedOptions.map((opt, i) => {
                  const lbl = typeof opt === 'object' ? opt.label : String(opt)
                  return <Tag key={i}>{lbl}</Tag>
                })
              ) : (
                <Tag>{selectedOptions.length} selezionati</Tag>
              )}
            </SelectedTags>
          ) : (
            <span style={{ fontSize: '13px', fontWeight: 500 }}>
              {selectedOptions.length} selezionati
            </span>
          )
        ) : (
          <span style={{ color: '#94a3b8' }}>{placeholder}</span>
        )}
        <Arrow $isOpen={isOpen}>▼</Arrow>
      </Trigger>

      {isOpen && createPortal(
        <OptionsList ref={optionsRef} style={{
          ...(dropdownPos.openAbove
            ? { bottom: dropdownPos.bottom, top: 'auto' }
            : { top: dropdownPos.top, bottom: 'auto' }),
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}>
          <div style={{ padding: '4px 4px 8px 4px', borderBottom: '1px solid #eee', marginBottom: '4px' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Cerca..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none'
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, i) => {
              const val = typeof opt === 'object' ? opt.value : opt
              const lbl = typeof opt === 'object' ? opt.label : String(opt)
              const isSelected = value.includes(val)

              return (
                <Option
                  key={`${val}-${i}`}
                  $isSelected={isSelected}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSelect(val)
                  }}
                >
                  <CheckBox
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                  />
                  {lbl}
                </Option>
              )
            })
          ) : (
            <div style={{ padding: '8px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
              Nessun risultato
            </div>
          )}
        </OptionsList>,
        document.body
      )}
    </Wrapper>
  )
}
